import Quill from 'quill';
import Delta from 'quill-delta';

export default class MagicUrl {
  private quill: Quill;
  private options;
  private defaults = {
    globalRegularExpression: /(https?:\/\/|www\.)[\S]+/g,
    urlRegularExpression: /(https?:\/\/[\S]+)|(www.[\S]+)/,
    normalizeRegularExpression: /(https?:\/\/[\S]+)|(www.[\S]+)/,
    normalizeUrlOptions: {
      stripFragment: false,
      stripWWW: false
    }
  };

  constructor(quill, options) {
    this.quill = quill;
    options = options || {};
    this.options = { ...this.defaults, ...options };
    this.registerTypeListener();
    this.registerPasteListener();
  }

  private registerPasteListener() {
    this.quill.clipboard.addMatcher(Node.TEXT_NODE, (node, delta) => {
      if (typeof node.data !== 'string') {
        return;
      }
      const matches = node.data.match(this.options.globalRegularExpression);
      if (matches && matches.length > 0) {
        const newDelta = new Delta();
        let str = node.data;
        matches.forEach((match) => {
          const split = str.split(match);
          const beforeLink = split.shift();
          newDelta.insert(beforeLink);
          newDelta.insert(match, { link: match });
          str = split.join(match);
        });
        newDelta.insert(str);
        delta.ops = newDelta.ops;
      }
      return delta;
    });
  }
  private registerTypeListener() {
    this.quill.on('text-change', (delta) => {
      const ops = delta.ops;
      // Only return true, if last operation includes whitespace inserts
      // Equivalent to listening for enter, tab or space
      if (!ops || ops.length < 1 || ops.length > 2) {
        return;
      }
      const lastOp = ops[ops.length - 1];
      if (!lastOp.insert || typeof lastOp.insert !== 'string' || !lastOp.insert.match(/\s/)) {
        return;
      }
      this.checkTextForUrl();
    });
  }
  private checkTextForUrl() {
    const sel = this.quill.getSelection();
    if (!sel) {
      return;
    }
    const [leaf] = this.quill.getLeaf(sel.index);
    if (!leaf.text || leaf.parent.domNode.localName === 'a') {
      return;
    }
    const urlMatch = leaf.text.match(this.options.urlRegularExpression);
    if (!urlMatch) {
      return;
    }
    const leafIndex = this.quill.getIndex(leaf);
    const index = leafIndex + urlMatch.index;

    this.textToUrl(index, urlMatch[0]);
  }
  private textToUrl(index, url) {
    const ops = new Delta().retain(index).delete(url.length).insert(url, { link: url });
    this.quill.updateContents(ops);
  }
}

// ===========Link Sanitizer===========

const Link = Quill.import('formats/link');

// Override the existing property on the Quill global object and add custom protocols
Link.PROTOCOL_WHITELIST = ['http', 'https'];

//Implement Quill link sanitizer to prevent relative paths
export class CustomLinkSanitizer extends Link {
  static sanitize(url: string) {
    // Run default sanitize method from Quill
    const sanitizedUrl = super.sanitize(url);
    let hasWhitelistedProtocol: boolean;

    // Not whitelisted URL based on protocol so, let's return `blank`
    if (!sanitizedUrl || sanitizedUrl === 'about:blank') {
      return sanitizedUrl;
    }

    Link.PROTOCOL_WHITELIST.forEach((protocol) => {
      if (sanitizedUrl.startsWith(protocol)) {
        hasWhitelistedProtocol = true;
      }
    });

    if (hasWhitelistedProtocol) {
      return sanitizedUrl;
    } else {
      //Append 'https' if the url doesn't have a protocol
      return `https://${sanitizedUrl}`;
    }
  }
}
